import React, { useState, useRef, useEffect } from 'react';

const Chat = () => {
    const [activeTab, setActiveTab] = useState('textChat');
    const [textMessages, setTextMessages] = useState([]);
    const [voiceMessages, setVoiceMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [withAudio, setWithAudio] = useState(true);
    const [status, setStatus] = useState('Ready to record...');
    const [isRecording, setIsRecording] = useState(false);
    const [latestAudioBuffer, setLatestAudioBuffer] = useState(null);
    const [playTextDisabled, setPlayTextDisabled] = useState(true);
    const [stopTextDisabled, setStopTextDisabled] = useState(true);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const audioBufferRef = useRef(null);
    const audioSourceRef = useRef(null);
    const textChatRef = useRef(null);
    const voiceChatRef = useRef(null);

    const sessionId = 'user123';
    const BASE_URL = 'https://api.mirai.healthcare/api/fee-service/';

    const initAudioContext = () => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }
    };

    const base64ToAudioBuffer = async (audioData) => {
        try {
            initAudioContext();
            if (audioData && audioData.type === "Buffer" && Array.isArray(audioData.data)) {
                const buffer = new Uint8Array(audioData.data);
                return await audioContextRef.current.decodeAudioData(buffer.buffer);
            } else if (typeof audioData === 'string') {
                const binaryString = window.atob(audioData);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                return await audioContextRef.current.decodeAudioData(bytes.buffer);
            }
            throw new Error('Invalid audio data format');
        } catch (error) {
            console.error('Error converting audio data:', error);
            throw error;
        }
    };

    const prepareAudioResponse = async (audioData) => {
        try {
            initAudioContext();
            audioBufferRef.current = await base64ToAudioBuffer(audioData);
            setPlayTextDisabled(false);
            setStopTextDisabled(true);
            setStatus('Audio ready to play');
        } catch (error) {
            console.error('Error preparing audio:', error);
            setStatus(`Error preparing audio playback: ${error.message}`);
            setPlayTextDisabled(true);
            setStopTextDisabled(true);
        }
    };

    const playAudioResponse = () => {
        if (!audioBufferRef.current) {
            setStatus('No audio available to play');
            return;
        }
        try {
            initAudioContext();
            audioSourceRef.current = audioContextRef.current.createBufferSource();
            audioSourceRef.current.buffer = audioBufferRef.current;
            audioSourceRef.current.connect(audioContextRef.current.destination);
            audioSourceRef.current.start(0);
            setPlayTextDisabled(true);
            setStopTextDisabled(false);
            audioSourceRef.current.onended = () => {
                setPlayTextDisabled(false);
                setStopTextDisabled(true);
            };
            setStatus('Playing audio...');
        } catch (error) {
            console.error('Error playing audio:', error);
            setStatus(`Error playing audio: ${error.message}`);
            setPlayTextDisabled(false);
            setStopTextDisabled(true);
        }
    };

    const stopAudioResponse = () => {
        if (audioSourceRef.current) {
            audioSourceRef.current.stop();
            setPlayTextDisabled(false);
            setStopTextDisabled(true);
        }
    };

    const sendTextMessage = async () => {
        if (!chatInput.trim()) return;
        const newMessages = [...textMessages, { type: 'user', message: chatInput, hasAudio: false }];
        setTextMessages(newMessages);
        setChatInput('');
        try {
            const response = await fetch(`${BASE_URL}chat/text?withAudio=${withAudio}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'session-id': sessionId,
                },
                body: JSON.stringify({ message: chatInput }),
            });
            const result = await response.json();
            if (result.success) {
                const hasAudio = !!result.data.audioBuffer;
                setTextMessages((prev) => [
                    ...prev,
                    { type: 'bot', message: result.data.text, hasAudio },
                ]);
                if (hasAudio) {
                    setLatestAudioBuffer(result.data.audioBuffer);
                    await prepareAudioResponse(result.data.audioBuffer);
                }
            } else {
                setTextMessages((prev) => [
                    ...prev,
                    { type: 'bot', message: `Error: ${result.error.message}`, hasAudio: false },
                    ]);
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            setTextMessages((prev) => [
                ...prev,
                { type: 'bot', message: 'Error sending message', hasAudio: false },
            ]);
        }
    };

    const clearHistory = async () => {
        try {
            const response = await fetch(`${BASE_URL}chat/history`, {
                method: 'DELETE',
                headers: { 'session-id': sessionId },
            });
            const result = await response.json();
            if (result.success) {
                setTextMessages([{ type: 'bot', message: 'Chat history cleared', hasAudio: false }]);
                setLatestAudioBuffer(null);
                setPlayTextDisabled(true);
                setStopTextDisabled(true);
            }
        } catch (error) {
            console.error('Failed to clear history:', error);
            setTextMessages((prev) => [
                ...prev,
                { type: 'bot', message: 'Error clearing chat history', hasAudio: false },
            ]);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];
            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };
            mediaRecorderRef.current.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const formData = new FormData();
                formData.append('audio', audioBlob, 'recording.webm');
                try {
                    setStatus('Uploading and processing...');
                    const response = await fetch(`${BASE_URL}voice/upload`, {
                        method: 'POST',
                        headers: { 'session-id': sessionId },
                        body: formData,
                    });
                    const result = await response.json();
                    if (result.success) {
                        setVoiceMessages((prev) => [
                            ...prev,
                            { type: 'user', message: result.data.userMessage, hasAudio: false },
                            { type: 'bot', message: result.data.botResponse, hasAudio: true },
                        ]);
                        setStatus('Processing complete!');
                        if (result.data.audioBuffer) {
                            await prepareAudioResponse(result.data.audioBuffer);
                        }
                    } else {
                        setStatus(`Error: ${result.error.message}`);
                    }
                } catch (error) {
                    console.error('Upload failed:', error);
                    setStatus('Error uploading or processing audio');
                }
            };
            mediaRecorderRef.current.start();
            setIsRecording(true);
            setStatus('Recording... Speak now...');
        } catch (error) {
            console.error('Failed to start recording:', error);
            setStatus('Error accessing microphone');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            setStatus('Processing recording...');
        }
    };

    useEffect(() => {
        if (activeTab === 'textChat' && textChatRef.current) {
            textChatRef.current.scrollTop = textChatRef.current.scrollHeight;
        } else if (activeTab === 'voiceChat' && voiceChatRef.current) {
            voiceChatRef.current.scrollTop = voiceChatRef.current.scrollHeight;
        }
    }, [textMessages, voiceMessages]);

    useEffect(() => {
        return () => {
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, []);

    const styles = {
        container: {
            maxWidth: '800px',
            margin: '20px auto',
            padding: '20px',
            fontFamily: 'Arial, sans-serif',
        },
        button: {
            padding: '10px 20px',
            margin: '5px',
            fontSize: '16px',
            cursor: 'pointer',
            backgroundColor: '#ffc0cb',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
        },
        buttonDisabled: {
            cursor: 'not-allowed',
            opacity: 0.6,
        },
        tab: {
            overflow: 'hidden',
            border: '1px solid #ccc',
            backgroundColor: '#ffc0cb',
            marginBottom: '20px',
        },
        tabButton: {
            backgroundColor: 'inherit',
            float: 'left',
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
            padding: '14px 16px',
            transition: '0.3s',
        },
        tabButtonActive: {
            backgroundColor: '#ffc0cb',
            color: 'white',
        },
        tabContent: {
            display: 'none',
            padding: '20px',
            border: '1px solid #ccc',
        },
        tabContentActive: {
            display: 'block',
        },
        status: {
            marginTop: '20px',
            padding: '10px',
            border: '1px solid #ccc',
            minHeight: '50px',
        },
        audioControls: {
            marginTop: '10px',
        },
        chatInput: {
            width: '100%',
            padding: '10px',
            margin: '10px 0',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxSizing: 'border-box',
        },
        chatHistory: {
            maxHeight: '300px',
            overflowY: 'auto',
            margin: '20px 0',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
        },
        message: {
            margin: '10px 0',
            padding: '10px',
            borderRadius: '4px',
        },
        userMessage: {
            backgroundColor: '#e3f2fd',
            marginLeft: '20%',
        },
        botMessage: {
            backgroundColor: '#f5f5f5',
            marginRight: '20%',
        },
        chatOptions: {
            margin: '10px 0',
            padding: '10px',
            backgroundColor: '#f9f9f9',
            borderRadius: '4px',
            border: '1px solid #eee',
        },
        checkboxLabel: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#666',
        },
        checkboxInput: {
            marginRight: '8px',
        },
        messageContainer: {
            position: 'relative',
        },
        audioBadge: {
            position: 'absolute',
            right: '5px',
            top: '5px',
            backgroundColor: '#ffc0cb',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '12px',
            fontSize: '12px',
            cursor: 'pointer',
        },
        voiceChatHistory: {
            maxHeight: '300px',
            overflowY: 'auto',
            margin: '20px 0',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
        },
        voiceMessageContainer: {
            position: 'relative',
            margin: '10px 0',
            clear: 'both',
            display: 'flex',
            alignItems: 'flex-end',
        },
        voiceMessage: {
            padding: '10px',
            borderRadius: '4px',
            maxWidth: '80%',
            wordWrap: 'break-word',
        },
        voiceUserMessage: {
            backgroundColor: '#e3f2fd',
            marginLeft: 'auto',
            borderRadius: '15px 15px 0 15px',
        },
        voiceBotMessage: {
            backgroundColor: '#f5f5f5',
            marginRight: '10px',
            borderRadius: '15px 15px 15px 0',
        },
        voiceAudioControls: {
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            marginBottom: '5px',
            marginLeft: '5px',
        },
        voiceAudioButton: {
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: '#ffc0cb',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
        voiceAudioButtonDisabled: {
            backgroundColor: '#e0e0e0',
            cursor: 'not-allowed',
            transform: 'none',
            boxShadow: 'none',
        },
        voiceAudioButtonSpan: {
            fontSize: '18px',
            color: 'white',
        },
    };

    return (
        <div style={styles.container}>
            <h2>Chat & Voice Test</h2>
            <div style={styles.tab}>
                <button
                    style={{
                        ...styles.tabButton,
                        ...(activeTab === 'textChat' ? styles.tabButtonActive : {}),
                    }}
                    onClick={() => setActiveTab('textChat')}
                >
                    Text Chat
                </button>
                <button
                    style={{
                        ...styles.tabButton,
                        ...(activeTab === 'voiceChat' ? styles.tabButtonActive : {}),
                    }}
                    onClick={() => setActiveTab('voiceChat')}
                >
                    Voice Chat
                </button>
            </div>

            <div style={{ ...styles.tabContent, ...(activeTab === 'textChat' ? styles.tabContentActive : {}) }}>
                <div style={styles.chatOptions}>
                    <label style={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={withAudio}
                            onChange={(e) => setWithAudio(e.target.checked)}
                            style={styles.checkboxInput}
                        />
                        Enable voice response
                    </label>
                </div>
                <div style={styles.chatHistory} ref={textChatRef}>
                    {textMessages.map((msg, index) => (
                        <div key={index} style={styles.messageContainer}>
                            <div
                                style={{
                                    ...styles.message,
                                    ...(msg.type === 'user' ? styles.userMessage : styles.botMessage),
                                }}
                            >
                                {msg.message}
                            </div>
                            {msg.hasAudio && (
                                <div
                                    style={styles.audioBadge}
                                    title="Click to play voice response"
                                    onClick={playAudioResponse}
                                >
                                    🔊
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <textarea
                    style={styles.chatInput}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendTextMessage())}
                    placeholder="Type your message here..."
                    rows="3"
                />
                <button style={styles.button} onClick={sendTextMessage}>
                    Send Message
                </button>
                <button style={styles.button} onClick={clearHistory}>
                    Clear History
                </button>
                <div style={styles.audioControls}>
                    <button
                        style={{ ...styles.button, ...(playTextDisabled ? styles.buttonDisabled : {}) }}
                        onClick={playAudioResponse}
                        disabled={playTextDisabled}
                    >
                        Play Latest Response
                    </button>
                    <button
                        style={{ ...styles.button, ...(stopTextDisabled ? styles.buttonDisabled : {}) }}
                        onClick={stopAudioResponse}
                        disabled={stopTextDisabled}
                    >
                        Stop Response
                    </button>
                </div>
            </div>

            <div style={{ ...styles.tabContent, ...(activeTab === 'voiceChat' ? styles.tabContentActive : {}) }}>
                <button
                    style={{ ...styles.button, ...(isRecording ? styles.buttonDisabled : {}) }}
                    onClick={startRecording}
                    disabled={isRecording}
                >
                    Start Recording
                </button>
                <button
                    style={{ ...styles.button, ...(!isRecording ? styles.buttonDisabled : {}) }}
                    onClick={stopRecording}
                    disabled={!isRecording}
                >
                    Stop Recording
                </button>
                <div style={styles.status}>{status}</div>
                <div style={styles.voiceChatHistory} ref={voiceChatRef}>
                    {voiceMessages.map((msg, index) => (
                        <div key={index} style={styles.voiceMessageContainer}>
                            <div
                                style={{
                                    ...styles.voiceMessage,
                                    ...(msg.type === 'user' ? styles.voiceUserMessage : styles.voiceBotMessage),
                                }}
                            >
                                {msg.message}
                            </div>
                            {msg.type === 'bot' && msg.hasAudio && (
                                <div style={styles.voiceAudioControls}>
                                    <button
                                        style={styles.voiceAudioButton}
                                        title="Play Response"
                                        onClick={playAudioResponse}
                                    >
                                        <span style={styles.voiceAudioButtonSpan}>▶</span>
                                    </button>
                                    <button
                                        style={{
                                            ...styles.voiceAudioButton,
                                            ...(stopTextDisabled ? styles.voiceAudioButtonDisabled : {}),
                                        }}
                                        title="Stop Response"
                                        onClick={stopAudioResponse}
                                        disabled={stopTextDisabled}
                                    >
                                        <span style={styles.voiceAudioButtonSpan}>■</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div style={styles.audioControls}>
                    <button
                        style={{ ...styles.button, ...(playTextDisabled ? styles.buttonDisabled : {}) }}
                        onClick={playAudioResponse}
                        disabled={playTextDisabled}
                    >
                        Play Response
                    </button>
                    <button
                        style={{ ...styles.button, ...(stopTextDisabled ? styles.buttonDisabled : {}) }}
                        onClick={stopAudioResponse}
                        disabled={stopTextDisabled}
                    >
                        Stop Response
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Chat;